import React, { useState, useEffect, useRef, useLayoutEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import FacetedSearchCompact from '../Components/FacetedSearchCompact';
import { query } from '../Actions/FacetedSearch.action';
import FilterTag from '../Components/FilterTags';
import { translate } from '../Services/translation';

const FacetedSearchCompactContainer = props => {
    const dispatch = useDispatch();
    const { facetFilters, productsViewCachedId } = useSelector((state) => state.facetedSearch);
    const previousCachedId = useRef(productsViewCachedId);
    const closeButtonRef = useRef(null);
    const filterRef = useRef(null); // Ref for the filter container
    const filterBackdropRef = useRef(null); // Ref for the filter container
    const mainContent = document.querySelector('main.main-content');
    const totalSelectedOptions = facetFilters?.reduce((total, filterGroup) => {
        return total + filterGroup.selectedOptions.length;
    }, 0) || 0;

    useEffect(() => {
        dispatch(query(window.location.search.substr(1) || '', false));
    }, [dispatch]);

    useLayoutEffect(() => {
        const checkAndRebind = () => {
            const externalToggleElement = document.getElementById('facetedSearchCompactToggle');

            if (externalToggleElement && !externalToggleElement.onclick) {
                bindExternalToggle(externalToggleElement);
                clearInterval(interval); // Stop checking once the binding is complete
            }
        };

        const interval = setInterval(checkAndRebind, 100); // Check every 100ms

        return () => {
            clearInterval(interval); // Clean up the interval when the component unmounts
        };
    }, [facetFilters]);

    const bindExternalToggle = (externalToggleElement) => {
        if (externalToggleElement) {
            externalToggleElement.onclick = (e) => {
                handleToggle();
            };
            const externalToggleElementSpan = externalToggleElement.querySelector('span');

            if (!externalToggleElementSpan) return;

            if (totalSelectedOptions > 0) {
                externalToggleElementSpan.innerText = totalSelectedOptions;
                externalToggleElementSpan.style.display = '';
            } else {
                externalToggleElementSpan.innerText = '';
                externalToggleElementSpan.style.display = 'none';
            }
        }
    };

    const handleToggle = () => {
        if (filterRef.current) {
            const filterElement = filterRef.current;

            if (filterElement.classList.contains('compact-filter__inner--open')) {
                filterElement.classList.remove('compact-filter__inner--open');
                filterElement.classList.add('compact-filter__inner--closed');
                filterBackdropRef.current.classList.remove('compact-filter__backdrop--open');
                filterBackdropRef.current.classList.add('compact-filter__backdrop--closed');
            } 
            else {
                filterElement.classList.remove('compact-filter__inner--closed');
                filterElement.classList.add('compact-filter__inner--open');
                filterBackdropRef.current.classList.remove('compact-filter__backdrop--open');
                filterBackdropRef.current.classList.add('compact-filter__backdrop--open');
                filterBackdropRef.current.classList.remove('compact-filter__backdrop--closed');
            }
        }

        // Since the default litium react component replaces the dom, animations stop working since components are technically
        // re-rendered. This is a workaround to add a class to the main which holds the filter open/closed state (not re-rendered)
        // This can be used to remove animation of open-state if the filter is open when component is re-rendered to avoid flickering.
        if (mainContent) {
            if (mainContent.classList.contains('main-content--has-filter-open')) {
                mainContent.classList.remove('main-content--has-filter-open');
                mainContent.classList.add('main-content--has-filter-closed');
            } 
            else {
                setTimeout(() => {
                    mainContent.classList.remove('main-content--has-filter-closed');
                    mainContent.classList.add('main-content--has-filter-open');
                }, 300);
            }
        }
    };

    // Handles re-rendering of result
    const onSearchResultDataChange = (dom) => {
        if ([null, undefined].includes(dom)) {
            return;
        }
        const container = document.createElement('div');
        container.innerHTML = dom;
        const existingResult = document.querySelector('#search-result');
        const tempResult = container.querySelector('#search-result');
        const existingFilter = existingResult.querySelector(
            '#facetedSearchCompact'
        );
        const tempFilter = tempResult.querySelector('#facetedSearchCompact');
        const replace = (node, newNode) => node.parentNode.replaceChild(newNode, node);

        // move existingFilter from existingResult to tempResult
        replace(tempFilter, existingFilter);
        // replace existingResult with tempResult ( newResult )
        replace(existingResult, tempResult);

        // bootstrap react components if any exists in the search result
        // window.__litium.bootstrapComponents();
        // Update 2024-12-06, the default filter component runs the bootstrapComponents function, which re-renderas all 
        // readct components on the page, including block components, minicart, navigation, etc. This is absolutely not 
        // optimal. This now runs a custom bootstrap function that only re-renders the product list inside the 
        // search result fileter component.
        window.__litium.bootstrapSearchResultFilterProductList();
    };
    useEffect(() => {
        const productViewCached = window.__litium.cache
            ? window.__litium.cache["PRODUCT_VIEW_CACHED"] || {}
            : {};

        if (!productViewCached.used) {
            productViewCached.used = true;
            const dom = productViewCached.productsView;
            dom && onSearchResultDataChange(dom);
        }
    });

    return (
        <div className='compact-filter'>
            <div
                ref={filterRef}
                className='compact-filter__inner'
            >
                <div className='compact-filter__heading'>
                    {translate('facet.header.filter')}
                    <button
                        ref={closeButtonRef}
                        className='hide-for-large'
                        onClick={handleToggle}
                    >
                        <span>{translate('navigation.close')}</span>
                        <span className='material-icons'>close</span>
                    </button>
                </div>

                <FilterTag />

                <FacetedSearchCompact
                    {...{
                        toggle: handleToggle,
                        previousCachedId,
                    }}
                />
            </div>

            <div
                className='compact-filter__backdrop'
                ref={filterBackdropRef}
                onClick={() => handleToggle()}
            />
        </div>
    );
};

export default FacetedSearchCompactContainer;
